import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import WorkflowUtils from "../../../utils/WorkflowUtils";
import WorkflowAPI from "../../api/WorkflowAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

const useCreateWorkflow = () => {
  const tenantID = useAtomValue(tenantIDAtom);
  const { cPermissions } = useCloudPermissions();
  const queryClient = useQueryClient();

  const createWorkflow = useMutation({
    mutationFn: ({ payload }: { payload: any; onSuccess?: () => void }) => {
      if (!cPermissions.cloud_create_workflow) {
        return Promise.reject(
          "User does not have permissions to create a workflow"
        );
      }

      const errors = WorkflowUtils.diagnoseWorkflow(payload.Actions);
      payload.HasError = errors ? Object.keys(errors)?.length > 0 : false;

      return WorkflowAPI.createWorkflow(payload);
    },
    onSuccess: (data, { onSuccess }) => {
      const workflow = data.data.data.workflow;

      queryClient.setQueryData(["workflow", workflow._id], workflow);
      queryClient.setQueryData<any>(["workflows", tenantID], (prev: any) => {
        const newWorkflow = {
          id: workflow?._id,
          _id: workflow?._id,
          Name: workflow?.Name,
          Description: workflow?.Description,
          Trigger: workflow?.Trigger,
        };

        if (!prev || !prev.length) {
          return [newWorkflow];
        }

        return [...prev, newWorkflow];
      });

      callFunctionIfExists(onSuccess, workflow._id);
    },
  });

  return {
    createWorkflow: createWorkflow.mutate,
    creatingWorkflow: createWorkflow.isPending,
    createError: createWorkflow.error
      ? getErrorMessage(createWorkflow.error)
      : null,
  };
};

export default useCreateWorkflow;
